import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  dispatch,
  ErrorVariants,
  FieldState,
  getChannel,
  getPaymentCustomerInfo,
  getWorkspaceId,
  showToast,
  updateEditPaymentDetails,
  useAppSelector,
  useLazyGetInvoiceParsedDataQuery,
} from '@zonofi/zono-money-store';
import { PaymentFieldKeys, PaymentModeType } from '../../../constants';
import { defaultPaymentDetails, paymentTypeFields } from '../../constant';
import moment from 'moment';
import dayjs from 'dayjs';
import { FileUploadOutput } from '@zonofi/common/types/interface';
import { getCurrencySign } from '@zonofi/common/functions/format-amount';
import { useUploadAndGetPresignedUrl } from '../../../hooks';
import { v4 as uuidv4 } from 'uuid';

interface UseParseImageFileProps {
  setPaymentState: Dispatch<
    SetStateAction<{
      paymentMode: PaymentModeType | null;
      paymentType: string;
    }>
  >;
  setImageParseState: Dispatch<
    SetStateAction<{
      shouldParseImage: boolean;
      imageIdToParse?: number;
    }>
  >;
  selectedMedia?: FileUploadOutput;
  uploadedMedia?: FileUploadOutput[];
}

export const useParsePaymentImage = ({
  selectedMedia,
  setPaymentState,
  setImageParseState,
  uploadedMedia,
}: UseParseImageFileProps) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const paymentCustomerInfo = useAppSelector(getPaymentCustomerInfo);
  const channel = useAppSelector(getChannel);

  const [parseImageAPI, parseImageAPIResponse] =
    useLazyGetInvoiceParsedDataQuery();

  const { uploadAndGetPresignedUrl } = useUploadAndGetPresignedUrl({
    customerId: paymentCustomerInfo?.inviteId as string,
  });

  const parseImageFile = async (media: FileUploadOutput) => {
    try {
      const presignedUrl = await uploadAndGetPresignedUrl(media);

      if (presignedUrl) {
        await parseWithUrl(media, presignedUrl || '');
      }
    } catch (error) {
      console.log('parseImageFile error:', error);
    }
  };

  const parseWithUrl = async (
    media: FileUploadOutput,
    presignedUrl: string
  ) => {
    const messageId = uuidv4();
    const contentType = media?.type || media?.file?.type || 'image/jpeg';
    return parseImageAPI({
      sellerWorkspaceId: sellerWorkspaceId,
      body: {
        activity: {
          type: 'upload',
          intent: 'payment',
          conversation: { id: messageId },
          channelId: channel?.id,
          inviteId: channel?.customerId,
          workspaceId: sellerWorkspaceId,
          sellerInviteId: channel?.customerId,
          text: 'Payment',
          isParse: true,
          file: {
            fileName: media?.file?.name || 'Payment File',
            mimeType: contentType,
            extension: '',
            url: presignedUrl,
          },
        },
      },
    }).unwrap();
  };

  useEffect(() => {
    if (
      parseImageAPIResponse.isSuccess &&
      !parseImageAPIResponse.isFetching &&
      !parseImageAPIResponse.isLoading &&
      parseImageAPIResponse.data
    ) {
      const { data } = parseImageAPIResponse;
      setImageParseState({
        shouldParseImage: false,
        imageIdToParse: selectedMedia?.lastModified,
      });
      if (data?.paymentMode) {
        const modeKey = Object.keys(PaymentModeType).find(
          (key) => key === data.paymentMode.toUpperCase()
        ) as keyof typeof PaymentModeType;

        const mode = PaymentModeType[modeKey] || data?.paymentMode;

        setPaymentState((prev) => ({
          ...prev,
          paymentMode: mode as PaymentModeType,
          paymentType: data?.paymentType || '',
        }));

        const allFields = [
          ...defaultPaymentDetails,
          ...(paymentTypeFields[mode] || []),
        ];

        const extractedData: Record<string, string> = {};
        allFields.forEach((field) => {
          const value = data?.[field?.key];

          switch (field.key) {
            case PaymentFieldKeys.PAYMENT_MODE:
              extractedData[field.key] = mode;
              break;

            case PaymentFieldKeys.PAYMENT_REFERENCE_CODE:
              extractedData[field.key] = value ? value : String(Date.now());
              break;

            case PaymentFieldKeys.AMOUNT_PAID:
              if (value) {
                const symbol = getCurrencySign() || '';
                const escaped = symbol.replace(
                  /[-\\/\\^$*+?.()|[\\]{}]/g,
                  '\\$&'
                );
                extractedData[field.key] = String(value).replace(
                  new RegExp(`^${escaped}\\s?`),
                  ''
                );
              } else {
                extractedData[field.key] = '';
              }
              break;

            case PaymentFieldKeys.PAYMENT_DATE:
              if (value && value !== 'Invalid Date') {
                const parsed = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD']);
                extractedData[field.key] = parsed.isValid()
                  ? parsed.format('DD/MM/YYYY')
                  : '';
              } else {
                extractedData[field.key] = '';
              }
              break;
            case PaymentFieldKeys.PAYMENT_TIME:
              if (value) {
                const normalized = String(value).trim().toUpperCase();
                const formats = ['hh:mm A', 'h:mm A', 'HH:mm', 'H:mm'];
                const parsed = formats
                  .map((fmt) => dayjs(normalized, fmt, true))
                  .find((d) => d.isValid());

                extractedData[field.key] = parsed ? parsed.format('HH:mm') : '';
              } else {
                extractedData[field.key] = '';
              }
              break;
            default:
              extractedData[field.key] = value ?? '';
          }
        });

        const fieldStateArray: FieldState[] = allFields.map((field) => ({
          ...field,
          value: extractedData[field.key] ?? '',
        }));

        dispatch(
          updateEditPaymentDetails({
            paymentDetails: fieldStateArray,
            paymentMedia: uploadedMedia ? uploadedMedia : [],
            paymentInvoice: [],
          })
        );
        setImageParseState({
          shouldParseImage: false,
          imageIdToParse: selectedMedia?.lastModified,
        });
      } else {
        dispatch(
          showToast({
            message: 'Unable to parse, try again',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
        setImageParseState((prev) => ({
          ...prev,
          shouldParseImage: false,
        }));
      }
    } else if (parseImageAPIResponse.isError) {
      dispatch(
        showToast({
          message: 'Unable to parse, try again',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
      setImageParseState((prev) => ({
        ...prev,
        shouldParseImage: false,
      }));
    }
  }, [parseImageAPIResponse]);

  return {
    parseImageFile,
  };
};
